import React, { useEffect, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import BeltIcon from './BeltIcon';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

const {
  FiX,
  FiInfo,
  FiBookOpen,
  FiPlay,
  FiStar,
  FiSun,
  FiMapPin,
  FiUsers,
  FiLoader,
  FiLayers,
  FiAward
} = FiIcons;

const DETAIL_FIELDS = [
  { key: 'represented', label: 'Representa', icon: FiAward },
  { key: 'esencia', label: 'Esencia', icon: FiAward },
  { key: 'formaYang', label: 'Forma Yang', icon: FiLayers },
  { key: 'trigrama', label: 'Trigrama', icon: FiStar },
  { key: 'atributo', label: 'Atributo', icon: FiSun },
  { key: 'relacionFamiliar', label: 'Relación familiar', icon: FiUsers },
  { key: 'puntoCardinal', label: 'Punto cardinal', icon: FiMapPin }
];

function AttributeBadge({ icon, label, value }) {
  if (!value) return null;

  return (
    <div className="bg-black/50 border border-white/15 rounded-2xl p-3 flex flex-col items-center justify-center text-center gap-1.5 hover:bg-white/5 transition-colors">
      <SafeIcon icon={icon} className="w-4 h-4 text-yellow-400 mb-1" />
      <span className="text-[10px] font-black text-white uppercase tracking-[0.2em] [text-shadow:_0_1px_4px_rgba(0,0,0,0.8)]">
        {label}
      </span>
      <span className="text-sm font-black text-white uppercase tracking-wider">
        {value}
      </span>
    </div>
  );
}

function DetailSection({ icon, title, children }) {
  if (!children) return null;

  return (
    <section className="bg-black/40 p-6 rounded-3xl border border-white/15">
      <div className="flex items-center gap-2 mb-4 text-yellow-400 uppercase font-black text-xs tracking-[0.2em] [text-shadow:_0_1px_4px_rgba(0,0,0,0.8)]">
        <SafeIcon icon={icon} />
        {title}
      </div>
      {children}
    </section>
  );
}

function getEmbedUrl(url) {
  if (!url || url === '#') return null;

  if (url.includes('drive.google.com')) {
    const fileId = url.match(/\/d\/([^/]+)/)?.[1];
    return fileId
      ? `https://drive.google.com/file/d/${fileId}/preview`
      : null;
  }

  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    let videoId = '';

    if (url.includes('watch?v=')) {
      videoId = url.split('v=')[1].split('&')[0];
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1].split('?')[0];
    }

    return videoId
      ? `https://www.youtube.com/embed/${videoId}?rel=0`
      : null;
  }

  return null;
}

export default function TechniqueModal({ technique, onClose }) {
  const [isLoading, setIsLoading] = useState(true);
  const { isAdmin } = useAuth();
  const [adminVideos, setAdminVideos] = useState({});

  useEffect(() => {
    if (!isAdmin || !technique?.videoSoloAdmin) return;
    api('/admin/videos').then(data => setAdminVideos(data.videos)).catch(() => {});
  }, [isAdmin, technique?.videoSoloAdmin]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    setIsLoading(true);
  }, [technique]);

  if (!technique) return null;

  const embedUrl = getEmbedUrl(technique.videoSoloAdmin ? adminVideos[technique.id] : technique.videoUrl);
  // "Representa" se muestra en las formas avanzadas, no en los Kicho (que también guardan ese dato).
  const showRepresents = technique.group !== 'Kichos';
  const availableDetails = DETAIL_FIELDS.filter(({ key }) => technique[key] && (key !== 'represented' || showRepresents));

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-8 bg-zinc-950/90 backdrop-blur-md"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 30 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 30 }}
          className="glass-modal w-full max-h-[90vh] md:max-w-3xl rounded-[2.5rem] overflow-hidden flex flex-col relative"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="p-6 md:p-8 border-b border-white/5 flex justify-between items-start md:items-center bg-white/5">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              {technique.beltType && (
                <BeltIcon
                  type={technique.beltType}
                  className="w-14 h-5 shadow-xl flex-shrink-0"
                />
              )}

              <div>
                <h2 className="text-2xl md:text-3xl font-black text-white kr-text uppercase tracking-tighter leading-none mb-1">
                  {technique.koreanName}
                </h2>

                <div className="flex items-center gap-2 flex-wrap">
                  {technique.spanishName && (
                    <p className="text-yellow-300 font-black tracking-[0.2em] uppercase text-xs [text-shadow:_0_1px_4px_rgba(0,0,0,0.8)]">
                      {technique.spanishName}
                    </p>
                  )}

                  {technique.rank && (
                    <span className="text-white text-xs uppercase tracking-widest [text-shadow:_0_1px_4px_rgba(0,0,0,0.8)]">
                      · {technique.rank}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <button
              onClick={onClose}
              className="p-3 bg-white/5 hover:bg-red-600 rounded-xl text-white transition-all active:scale-90 flex-shrink-0 mt-1 md:mt-0"
            >
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6 md:space-y-8 custom-scrollbar">
            {technique.image && (
              <div className="bg-white rounded-3xl overflow-hidden border border-white/5 shadow-2xl">
                <img
                  src={technique.image}
                  alt={technique.spanishName || technique.koreanName}
                  className="w-full h-auto object-contain"
                />
              </div>
            )}

            {(embedUrl || !technique.image) && (
            <div className="aspect-video bg-black rounded-3xl relative overflow-hidden border border-white/5 shadow-2xl">
              {embedUrl ? (
                <>
                  {isLoading && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-zinc-900 z-10">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          ease: 'linear'
                        }}
                      >
                        <SafeIcon icon={FiLoader} className="w-8 h-8 text-red-600" />
                      </motion.div>
                    </div>
                  )}

                  <iframe
                    src={embedUrl}
                    className="w-full h-full"
                    allow="autoplay; encrypted-media; fullscreen"
                    title={`Video de ${technique.spanishName || technique.koreanName}`}
                    onLoad={() => setIsLoading(false)}
                  />
                </>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white/20 p-8 text-center bg-white/5">
                  <SafeIcon icon={FiPlay} className="w-12 h-12 mb-4" />
                  <span className="text-[10px] font-black uppercase tracking-[0.3em]">
                    Video no disponible por el momento
                  </span>
                  <span className="mt-2 text-xs text-white/30">
                    ¡Regresa pronto!
                  </span>
                </div>
              )}
            </div>
            )}

            {availableDetails.length > 0 && (
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                {availableDetails.map(({ key, label, icon }) => (
                  <AttributeBadge
                    key={key}
                    icon={icon}
                    label={label}
                    value={technique[key]}
                  />
                ))}
              </div>
            )}

            {technique.rank && (
              <div className="flex items-center gap-3 px-5 py-4 rounded-2xl bg-black/40 border border-red-400/50">
                <SafeIcon icon={FiAward} className="text-red-500" />
                <div>
                  <span className="block text-[11px] font-black uppercase tracking-[0.2em] text-red-300">
                    Nivel
                  </span>
                  <span className="text-base font-black text-white">
                    {technique.rank}
                  </span>
                </div>
              </div>
            )}

            <div className="grid gap-6">
              <DetailSection icon={FiBookOpen} title="Significado">
                {technique.meaning && (
                  <p className="text-white italic text-xl font-semibold leading-relaxed">
                    "{technique.meaning}"
                  </p>
                )}
              </DetailSection>

              <DetailSection icon={FiInfo} title="Descripción">
                {technique.description && (
                  <p className="text-white text-base leading-relaxed font-medium">
                    {technique.description}
                  </p>
                )}
              </DetailSection>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}